import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { DocumentTextIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionContext";
import { useFeatureFlag } from "../contexts/FeatureFlagContext";
import { FeatureFlag } from "../constants/featureFlags";
import Button from "../componentLibrary/Button";
import Explore from "../assets/icons/explore_icon.svg";
import Dashboard from "../assets/icons/dashboard_icon.svg";
import EmpowermentIcon from "../assets/icons/empowerment_mountain_footsteps.svg";
import { default as ProfileIcon } from "../assets/icons/profile_icon.svg";
import SleepIcon from "../assets/icons/sleep_moon_bed_zzz.svg";
import { default as Use } from "../assets/icons/use_pfl_icon.svg";
import AboutUsNavItem from "./AboutUsNavItem";

const SideNav = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const showNewToolboxes = useFeatureFlag(FeatureFlag.NEW_TOOLBOXES);

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setIsCollapsed(true);
    }
  };

  const { isAuthenticated: isAuth, logout: authLogout } = useAuth();

  const handleLogout = async () => {
    await authLogout();
    window.location.reload();
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  // Use RBAC permission context instead of getIsAdmin
  const { hasPermission, isOwner } = usePermissions();

  // Check if user has any admin permissions
  const hasAnyAdminPermission =
    hasPermission("analytics:read") ||
    hasPermission("content:*") ||
    hasPermission("content:create") ||
    hasPermission("content:read") ||
    hasPermission("content:update") ||
    hasPermission("content:delete") ||
    hasPermission("accessCode:read") ||
    hasPermission("announcement:*") ||
    isOwner;

  return (
    <>
      <Bars3Icon onClick={toggleSidebar} className="h-6 w-6 text-gray-700" />
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full transition-all duration-300 ${
          isCollapsed ? "w-0" : "w-72 p-4"
        } bg-blue-50 overflow-scroll shadow-lg z-[9999]`}
      >
        {!isAuth ? (
          <div
            className={`transition-opacity duration-300 space-y-8 ${
              isCollapsed ? "opacity-0" : "opacity-100"
            }`}
          >
            <Button variant="alternative" size="sm" onClick={toggleSidebar}>
              <XMarkIcon color="black" className="h-5 w-5" />
            </Button>
            <div>
              <ul className="mt-4 space-y-4 divide-y divide-white">
                <li className="flex items-center space-x-2 pt-2">
                  <img
                    src={Explore}
                    alt="A lightbulb"
                    className="w-6 h-6 mr-1"
                  />
                  <Link
                    onClick={toggleSidebar}
                    to="/"
                    className={`${
                      isActive("/") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>Home</span>
                  </Link>
                </li>
                <li className="flex items-center space-x-2 pt-2">
                  <img
                    src={EmpowermentIcon}
                    alt="Business"
                    className="w-6 h-6 mr-1"
                  />
                  <Link
                    onClick={toggleSidebar}
                    to="/business"
                    className={`${
                      isActive("/business") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>
                      Psych For Life<sup>®</sup> For Business
                    </span>
                  </Link>
                </li>
                <AboutUsNavItem onClick={toggleSidebar} isActive={isActive} />
                <li className="flex items-center space-x-2 pt-2">
                  <img src={Dashboard} alt="Login" className="w-6 h-6 mr-1" />
                  <Link
                    onClick={toggleSidebar}
                    to="/login"
                    className={`${
                      isActive("/login") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>Log In</span>
                  </Link>
                </li>
                <li className="flex items-center space-x-2 pt-2">
                  <DocumentTextIcon className="w-6 h-6 mr-1" />
                  <Link
                    onClick={toggleSidebar}
                    to="/legal"
                    className={`${
                      isActive("/legal") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>Legal and Privacy Policy</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div
            className={`transition-opacity duration-300 space-y-8 ${
              isCollapsed ? "opacity-0" : "opacity-100"
            }`}
          >
            <Button
              onClick={toggleSidebar}
              variant="alternative"
              size="sm"
              className="flex items-center"
            >
              <h2 className="text-black">Menu</h2>
            </Button>
            <div>
              <ul className="mt-4 space-y-4 divide-y divide-white">
                {/* Main navigation */}
                <li className="flex w-64 items-center space-x-2 pt-4">
                  <img
                    src={Explore}
                    alt="A lightbulb"
                    className="w-6 h-6 mr-1"
                  />
                  <Link
                    onClick={toggleSidebar}
                    to="/explore"
                    className={`${
                      isActive("/explore") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>Explore</span>
                  </Link>
                </li>

                {/* My Toolboxes section */}
                {showNewToolboxes && (
                  <li className="pt-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-1">
                      My Toolboxes
                    </span>
                    <ul className="mt-2 space-y-3">
                      <li className="flex w-64 items-center space-x-2">
                        <img
                          src={SleepIcon}
                          alt="A moon with zzz"
                          className="w-6 h-6 mr-1"
                        />
                        <Link onClick={toggleSidebar} to="/toolboxes/sleep">
                          <span>Sleep</span>
                        </Link>
                      </li>
                      <li className="flex w-64 items-center space-x-2">
                        <img
                          src={SleepIcon}
                          alt="Anxiety"
                          className="w-6 h-6 mr-1"
                        />
                        <Link onClick={toggleSidebar} to="/toolboxes/anxiety">
                          <span>Anxiety</span>
                        </Link>
                      </li>
                      <li className="flex w-64 items-center space-x-2">
                        <img
                          src={SleepIcon}
                          alt="Gratitude"
                          className="w-6 h-6 mr-1"
                        />
                        <Link onClick={toggleSidebar} to="/toolboxes/gratitude">
                          <span>Gratitude</span>
                        </Link>
                      </li>
                      <li className="flex w-64 items-center space-x-2">
                        <img
                          src={SleepIcon}
                          alt="Goals"
                          className="w-6 h-6 mr-1"
                        />
                        <Link onClick={toggleSidebar} to="/toolboxes/goals">
                          <span>Goals</span>
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}

                {/* Account & help */}
                <li className="flex w-64 items-center space-x-2 pt-4">
                  <img src={Use} alt="A map" className="w-6 h-6 mr-1" />
                  <Link
                    onClick={toggleSidebar}
                    to="/tutorial"
                    className={`${
                      isActive("/tutorial") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>
                      How to Use Psych for Life<sup>®</sup>
                    </span>
                  </Link>
                </li>
                <li className="flex w-64 items-center space-x-2 pt-4">
                  <img
                    src={ProfileIcon}
                    alt="Profile"
                    className="w-6 h-6 mr-1"
                  />
                  <Link
                    onClick={toggleSidebar}
                    to="/profile"
                    className={`${
                      isActive("/profile") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>My Profile</span>
                  </Link>
                </li>
                <AboutUsNavItem onClick={toggleSidebar} isActive={isActive} />

                {/* Admin */}
                {hasAnyAdminPermission && (
                  <li className="flex w-64 items-center space-x-2 pt-4">
                    <ShieldCheckIcon className="w-6 h-6 mr-1" />
                    <Link
                      onClick={toggleSidebar}
                      to="/admin"
                      className={`${
                        isActive("/admin")
                          ? "font-bold text-blue-500 border-b-2 border-gray-300"
                          : "border-b-2 border-gray-300"
                      }`}
                    >
                      <span>Admin</span>
                    </Link>
                  </li>
                )}

                {/* Legal */}
                <li className="flex w-64 items-center space-x-2 pt-4">
                  <DocumentTextIcon className="w-6 h-6 mr-1" />
                  <Link
                    onClick={toggleSidebar}
                    to="/legal"
                    className={`${
                      isActive("/legal") ? "font-bold text-blue-500" : ""
                    }`}
                  >
                    <span>Legal and Privacy Policy</span>
                  </Link>
                </li>
              </ul>
            </div>
            <div className="pb-2">
              <Button variant="alternative" onClick={handleLogout}>
                Sign Out
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SideNav;
